import React from 'react';
import {
    EBirdChecklistByFamily,
    EBirdMyDataSchema,
    EBirdObservationsByLocation,
    EBirdObservationsBySpecies
} from "ebird-mydata-reader";
import {AppScreen} from "./app-screen";
import Welcome from "./views/welcome";
import UploadData from "./views/upload-data";
import SelectTimePeriod from "./views/select-time-period";
import MapExplorer from "./views/map";
import WrapUp from "./views/wrap-up";
import BigYearExplorer from "./views/big-year-explorer";
import Checklist from "./views/checklist";
import {EBirdTaxonomyEntry} from "./helpers/useEBirdTaxonomy";
import LoadingData from "./views/loading-data";
import type {SpeciesFamilyLink} from "./App";

export type AppRouterProps = {
    screen: AppScreen,
    rawData: EBirdMyDataSchema[],
    lifeList: EBirdMyDataSchema[],
    years: number[]|string[],
    months: number[]|string[],
    year?: number|string,
    month?: number|string,
    mapMode?: string,
    mapActiveSpecies?: number,
    filteredObservations: EBirdMyDataSchema[],
    observationsBySpecies: EBirdObservationsBySpecies[],
    observationsByLocation: EBirdObservationsByLocation[],
    checklist: EBirdChecklistByFamily[],
    familyByTaxonomicOrder: Map<number, SpeciesFamilyLink>,
    parentTaxonomicOrderByTaxonomicOrder: Map<number, number>,
    speciesImages: any,
    getSpeciesImages: (markers: any) => void,
    lookupByTaxonomicOrder: (taxonomicOrder: number) => EBirdTaxonomyEntry|undefined,
    onRequestData: () => void,
    onShowUploadData: () => void,
    onDataLoaded: (data: any[]) => void,
    onSetYears: React.Dispatch<any>,
    onResetDataRequest: () => void,
    onSetMonth: React.Dispatch<any>,
    onSetYear: React.Dispatch<any>,
    onSetTimePeriod: (year: string, month: string) => void,
    onSetSpeciesFilter: (taxonomicOrder: number) => EBirdObservationsBySpecies|undefined,
    onResetStoredData: () => void,
    onStartBigYear: () => void,
    onShowWrapUp: () => void,
    onShowTimePeriodSelector: () => void,
    onShowChecklist: () => void
}

export default function AppRouter({
    screen,
    rawData,
    lifeList,
    years,
    months,
    year,
    month,
    mapMode,
    mapActiveSpecies,
    filteredObservations,
    observationsBySpecies,
    observationsByLocation,
    checklist,
    familyByTaxonomicOrder,
    parentTaxonomicOrderByTaxonomicOrder,
    speciesImages,
    getSpeciesImages,
    lookupByTaxonomicOrder,
    onRequestData,
    onShowUploadData,
    onDataLoaded,
    onSetYears,
    onResetDataRequest,
    onSetMonth,
    onSetYear,
    onSetTimePeriod,
    onResetStoredData,
    onStartBigYear,
    onShowWrapUp,
    onShowTimePeriodSelector,
    onShowChecklist
}: AppRouterProps) {
    switch (screen) {
        case AppScreen.Loading:
            return <LoadingData />;

        case AppScreen.Welcome:
            return <Welcome onRequestData={onRequestData} onShowUploadData={onShowUploadData} />;

        case AppScreen.Upload:
            return (
                <UploadData
                    onSetData={onDataLoaded}
                    onSetYears={onSetYears}
                    onResetDataRequest={onResetDataRequest}
                />
            );

        case AppScreen.SelectTimePeriod:
            return (
                <SelectTimePeriod
                    lifeList={lifeList}
                    rawData={rawData}
                    checklist={checklist}
                    speciesImages={speciesImages}
                    years={years}
                    months={months}
                    onSetMonth={onSetMonth}
                    onSetYear={onSetYear}
                    onSetTimePeriod={onSetTimePeriod}
                    onResetStoredData={onResetStoredData}
                    onShowWrapUp={onShowWrapUp}
                    onShowChecklist={onShowChecklist}
                />
            );

        case AppScreen.Map:
            return (
                <MapExplorer
                    observations={filteredObservations}
                    year={year}
                    month={month}
                    observationsByLocation={observationsByLocation}
                    familyByTaxonomicOrder={familyByTaxonomicOrder}
                    parentTaxonomicOrderByTaxonomicOrder={parentTaxonomicOrderByTaxonomicOrder}
                    onStartBigYear={onStartBigYear}
                    onShowTimePeriodSelector={onShowTimePeriodSelector}
                    mapMode={mapMode}
                    activeSpecies={mapActiveSpecies}
                    speciesImages={speciesImages}
                    getSpeciesImages={getSpeciesImages}
                    lookupByTaxonomicOrder={lookupByTaxonomicOrder}
                />
            );

        case AppScreen.WrapUp:
            return (
                <WrapUp
                    observations={filteredObservations}
                    year={year}
                    month={month}
                />
            );

        case AppScreen.BigYear:
            return (
                <BigYearExplorer
                    observations={filteredObservations}
                    speciesImages={speciesImages}
                    year={year}
                    lookupByTaxonomicOrder={lookupByTaxonomicOrder}
                />
            );

        case AppScreen.Checklist:
            return <Checklist checklist={checklist} observationsBySpecies={observationsBySpecies} />;
    }
}
